import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from './ConfirmModal';
import DescriptionEditModal from './DescriptionEditModal';
import ImagePickerModal from './ImagePickerModal';
import CoverCropperModal from './CoverCropperModal';
import { DescriptionIcon, descBadgeStyle, descBadgeHover } from './DescriptionBadge';
import { useProjectLoading } from '../contexts/ProjectLoadingContext'; // 🆕 全局加载状态
import {
    X, Music, Sparkles, ChevronDown, ChevronRight,
  Briefcase, Globe, Database, Layers, ArrowLeftCircle,
  UploadCloud, DownloadCloud, MousePointerClick,
  FolderOpen, LayoutGrid, Plus, Pencil, Trash2, Image, Check, Loader2,
    Edit2, HardDrive, RefreshCw
} from 'lucide-react';
import localCacheService from '../services/localCacheService';
import { IconFilm } from '../editorUtils';
import { useTheme } from '../hooks/useTheme';
import { resolveBgForTheme } from '../node-parts/TextNodeView';

// ════════════════════════════════════════════════════════════════════
// 📡 项目状态指示器（小尺寸版本，用于 EditorPage 右侧抽屉的舞台卡片）
//
// 数据来源：allProjects state → 由 useWebSocket.js 的 project-status-changed 事件
//           用 pendingDelta 增量更新。初始值在 EditorPage 加载时从 API 获取。
//
// 与 DashboardPage 的 ProjectStatusIndicator 区别：
//   - 这里的 pendingTasks 通过 useWebSocket 增量(delta)维护（WebSocket 始终在线，不丢事件）
//   - DashboardPage 的通过绝对值维护（可能丢事件，需要兜底）
// ════════════════════════════════════════════════════════════════════
const ProjectStatusIndicator = ({ pendingTasks, hasUnseenCompleted, size = 'small' }) => {
    const indicatorSize = size === 'small' ? 20 : 28;
    const iconSize = size === 'small' ? 12 : 16;
    const badgeSize = size === 'small' ? 12 : 16;
    
    if (pendingTasks > 0) {
        return (
            <div style={{
                position: 'absolute', bottom: 4, right: 4,
                background: 'rgba(59, 130, 246, 0.9)', borderRadius: '50%',
                width: indicatorSize, height: indicatorSize, 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(59, 130, 246, 0.5)'
            }}>
                <Loader2 size={iconSize} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
                {pendingTasks > 1 && (
                    <span style={{
                        position: 'absolute', top: -3, right: -3,
                        background: '#ef4444', color: '#fff', fontSize: 8, fontWeight: 'bold',
                        borderRadius: '50%', width: badgeSize, height: badgeSize,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>{pendingTasks}</span>
                )}
            </div>
        );
    }
    
    if (hasUnseenCompleted) {
        return (
            <div style={{
                position: 'absolute', bottom: 4, right: 4,
                background: 'rgba(34, 197, 94, 0.9)', borderRadius: '50%',
                width: indicatorSize, height: indicatorSize, 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(34, 197, 94, 0.5)'
            }}>
                <Check size={iconSize} color="#fff" strokeWidth={3} />
            </div>
        );
    }
    
    return null;
};

// 悬浮手柄组件
const ToggleHandle = ({ onClick, isOpen }) => {
  const [hover, setHover] = useState(false);
  return (
    <div 
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={isOpen ? "收起仓库" : "打开仓库"}
      style={{
        position: 'absolute',
        left: '-52px', // 悬浮在抽屉左侧
        top: '50%',
        transform: 'translateY(-50%)',
        width: '40px', height: '40px',
        borderRadius: '50%', // 小圆圈
        background: 'var(--toolbar-bg)', // 深邃蓝
        border: '1px solid var(--border-strong)',
        boxShadow: hover ? '0 0 15px rgba(59, 130, 246, 0.4)' : '-4px 0 12px rgba(0,0,0,0.3)', // 悬浮发光
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        color: hover ? 'var(--text-hover)' : 'var(--text-secondary)',
        transition: 'all 0.2s',
        zIndex: 3001
      }}
    >
      {isOpen ? <ChevronRight size={22} /> : <Database size={20} />}
    </div>
  );
};

const styles = {
  drawer: (isOpen, bottomOffset = 0) => ({
    position: 'fixed', right: 0, top: 0, bottom: bottomOffset, width: '320px',
    background: 'var(--toolbar-bg)', backdropFilter: 'blur(12px)', // 统一深邃蓝 (#1e293b 的 RGBA 版)
    borderLeft: '1px solid var(--border-subtle)', zIndex: 3000,
    boxShadow: 'var(--drawer-shadow)', display: 'flex', flexDirection: 'column',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
    userSelect: 'none', WebkitUserSelect: 'none' // 【修复】防止框选舞台时意外选中资源库文字
  }),
  sectionHeader: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 20px 4px', color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 'bold', letterSpacing: '1px' },
  subCategory: { marginBottom: '0px' },
  subHeader: { display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 20px', cursor: 'pointer', color: 'var(--folder-title)', fontSize: '13px', transition: 'background 0.2s', userSelect: 'none' },
  grid: { display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '4px 12px 8px' },
  
  card: (categoryId, opts = {}) => {
    // forcePrompt：用于自定义咒语分类（非 'prompt' id）也走小卡片样式
    const isPrompt = categoryId === 'prompt' || opts.forcePrompt === true;
    // 【修改】音频卡片 (Music/SFX/Voice) 也设为 64x64 的小卡片
    const isAudio = categoryId === 'music' || categoryId === 'sfx' || categoryId === 'voice';
    const isRole = categoryId === 'role';

    const width = (isPrompt || isAudio) ? '64px' : '86px';
    const height = (isPrompt || isAudio) ? '64px' : (isRole ? '142px' : '86px'); // Role 维持约 3:5 比例

    return {
      width, height,
      background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-subtle)', borderRadius: '0px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'border 0.2s'
    };
  },
  
  // 删除按钮：右上角
  // 卡片右上角关闭：与画布节点右上角关闭按钮同款（深色半透明+毛玻璃+白 X，两模式一致）
  removeBtn: { position: 'absolute', top: '3px', right: '3px', width: '18px', height: '18px', background: 'rgba(0, 0, 0, 0.35)', backdropFilter: 'blur(4px)', border: '0.5px solid rgba(255, 255, 255, 0.1)', borderRadius: '4px', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardLabel: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: '10px', textAlign: 'center', padding: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },

  // 选择圈样式
  checkCircle: (selected) => ({
    position: 'absolute', top: '4px', left: '4px',
    width: '14px', height: '14px', borderRadius: '50%', // 缩小了尺寸
    background: selected ? '#3b82f6' : 'rgba(0,0,0,0.4)',
    border: selected ? '1.5px solid #fff' : '1.5px solid rgba(255,255,255,0.6)', // 略微调细边框
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 20
  })
};

// --- 可编辑标题组件 ---
// 🎬 canEdit: 是否允许编辑（不传则默认允许）
const EditableLabel = ({ title, onUpdate, style, canEdit = true }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [val, setVal] = useState(title);
  
  useEffect(() => setVal(title), [title]);

  const commit = () => {
    setIsEditing(false);
    if (val !== title && val.trim()) {
       onUpdate(val);
    } else {
       setVal(title); // revert if empty or same
    }
  };

  if (isEditing) {
    return (
      <input
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if(e.key === 'Enter') commit(); }}
        autoFocus
        onClick={e => e.stopPropagation()} 
        style={{
           ...style,
           background: '#09090b', color: '#fff', border: 'none', 
           outline: '1px solid #3b82f6', borderRadius: '0', 
           padding: '1px 2px', width: '100%', boxSizing: 'border-box',
           textAlign: 'center', zIndex: 50 // 确保在最上层
        }}
      />
    );
  }

  return (
    <div 
      style={style} 
      title={canEdit ? `双击重命名: ${title}` : title}
      onDoubleClick={(e) => { 
        if (!canEdit) return; // 🎬 不可编辑时忽略双击
        e.stopPropagation(); 
        setIsEditing(true); 
      }}
    >
      {title}
    </div>
  );
};

// --- 同步画布使用的自定义图标 (SVG ICON) ---
const IconMusic = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke={color} strokeWidth="4" style={{ display: 'block' }}>
    <path d="M24 44c11.046 0 20-8.954 20-20S35.046 4 24 4S4 12.954 4 24s8.954 20 20 20Z"/><path strokeLinecap="round" strokeLinejoin="round" d="M26 14v14"/><path strokeLinejoin="round" d="M14 28.666C14 26.64 15.934 25 18.32 25H26v4.334C26 31.36 24.066 33 21.68 33h-3.36C15.934 33 14 31.359 14 29.334z"/><path strokeLinecap="round" strokeLinejoin="round" d="m32 15l-6-1"/>
  </svg>
);
const IconPhoto = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <path d="M15 8h.01M4 15l4-4c.928-.893 2.072-.893 3 0l5 5"/><path d="m14 14l1-1c.928-.893 2.072-.893 3 0l2 2"/><path d="M3 12a9 9 0 0 0 9 9a9 9 0 0 0 9-9a9 9 0 0 0-9-9a9 9 0 0 0-9 9"/>
  </svg>
);

// --- 音频卡片组件 (纯展示，不支持播放；悬浮大圆按钮拖到舞台播放) ---
const AudioCard = memo(({ item }) => {
  return (
    <div 
      style={{
        width: '100%', height: '100%', 
        backgroundColor: '#475569',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
        padding: '4px', boxSizing: 'border-box', gap: '4px' 
      }}
    >
      {/* 音频图标（静态展示） */}
      <Music size={20} color="rgba(255,255,255,0.35)" />

      <div style={{
          fontSize: '10px', color: '#e2e8f0', fontWeight: 'bold', textAlign: 'center',
          display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical', 
          overflow: 'hidden', lineHeight: '1.2', height: '24px', padding: '0 1px'
      }}>
          {item.title || "音频"}
      </div>
    </div>
  );
});

const LibraryFolder = ({
  title, icon: Icon, categoryId, assets = [],
  onSpawn, onDelete, onUpdate,
  canDelete = true, // 🎬 是否显示删除按钮
  hideSpawn = false, // 🎬 剧组库隐藏「转移到舞台」按钮，需先下载到后台
  isSelectionMode, selectedIds, onToggleSelect,
  forcePrompt = false, // 🆕 自定义咒语分类强制按 prompt 样式（64x64）渲染卡片
  user, // 🆕 用户信息（用于权限判断）
  onSetAssetCover, // 🆕 设置资产封面回调
  onEditDescription, // 🆕 编辑描述词回调（点卡片右下角小标签）
  onEditAsset, // 🆕 编辑资产回调
  activeTab, // 🆕 当前Tab（用于判断是否显示编辑按钮）
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [hoveredId, setHoveredId] = useState(null); // Track hover state
  const [theme] = useTheme(); // 🎨 咒语卡底色按主题翻译（深调↔浅调配对）
  const items = useMemo(() => assets.filter(a => a.category === categoryId), [assets, categoryId]);

  return (
    <div style={styles.subCategory}>
      <div style={styles.subHeader} onClick={() => setIsOpen(!isOpen)}>
        {/* 用传入的 Icon 替换文件夹图标 */}
        <Icon size={18} color={items.length > 0 ? '#999' : '#555'} />
        <span>{title}</span>
        {/* 展开箭头放到标题后面 */}
        {isOpen ? <ChevronDown size={14} color="var(--text-muted)" /> : <ChevronRight size={14} color="var(--text-muted)" />}
        <span style={{flex: 1}}></span> {/* 弹簧撑开右侧数字 */}
        <span style={{fontSize: '10px', color: '#444'}}>{items.length}</span>
      </div>

      {isOpen && items.length > 0 && (
        <div style={styles.grid}>
          {items.map((item) => {
            const isSelected = selectedIds && selectedIds.has(item.id);
            const isHovered = hoveredId === item.id;

            return (
              <div 
                key={item.id} 
                style={{
                    ...styles.card(categoryId, { forcePrompt }),
                    borderColor: isSelected ? '#3b82f6' : 'var(--border-subtle)',
                    boxShadow: isSelected ? '0 0 0 2px rgba(59, 130, 246, 0.4)' : 'none'
                }}
                title={isSelectionMode ? `选中: ${item.title}` : undefined}
                onClick={() => {
                   if (isSelectionMode) {
                        onToggleSelect(item.id);
                   } 
                   // Remove click-to-spawn to avoid conflict with editing
                }}
                onMouseEnter={e => {
                    setHoveredId(item.id);
                    if (!isSelected) e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onMouseLeave={e => {
                    setHoveredId(null);
                    if (!isSelected) e.currentTarget.style.borderColor = 'var(--border-subtle)';
                }}
              >
                {/* 1. 选框逻辑 */}
                {isSelectionMode && (
                  <div style={styles.checkCircle(isSelected)}>
                     {isSelected && <div style={{width:'8px', height:'8px', borderRadius:'50%', background:'#fff'}} />}
                  </div>
                )}
                
                {/* 1.5 悬浮生成按钮 (Transfer to Canvas) — 剧组库隐藏，需先下载到后台 */}
                {isHovered && !isSelectionMode && !hideSpawn && (
                    <div 
                        title="转移到舞台"
                        style={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            // background: 'rgba(0,0,0,0.4)', // 移除全覆盖蒙层，避免"选中整张卡"的感觉
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            zIndex: 15, cursor: 'default',
                            pointerEvents: 'none' // 【关键】让点击穿透，方便点选下方文字
                        }}
                    >
                        <div 
                            onClick={(e) => { e.stopPropagation(); onSpawn(item); }}
                            style={{
                                pointerEvents: 'auto', // 按钮自身恢复点击响应
                                background: 'rgba(0, 0, 0, 0.25)', // 半透明底
                                backdropFilter: 'blur(4px)', // 毛玻璃
                                border: '1px solid rgba(255,255,255,0.3)',
                                borderRadius: '50%', width: '36px', height: '36px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)', 
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'scale(1.1)';
                                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)';
                                e.currentTarget.style.borderColor = '#fff';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.25)';
                                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                            }}
                        >
                             <ArrowLeftCircle size={20} color="#fff" strokeWidth={1.5} />
                        </div>
                    </div>
                )}
                
                {/* 2. 内容渲染 */}
                {item.type === 'workflow' ? (
                  // 🖼️ 工作流：缩略图 + 叠加图标和标题
                  (item.localThumbUrl || item.thumbnailUrl) ? (
                    <div style={{position: 'relative', width:'100%', height:'100%'}}>
                      {/* 背景缩略图 */}
                      <img
                          src={item.localThumbUrl || item.thumbnailUrl}
                          style={{width:'100%', height:'100%', objectFit:'cover'}}
                          alt={item.title || "工作流"}
                      />
                      {/* 叠加层：半透明遮罩 + 图标 + 标题 */}
                      <div style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          padding: '6px', boxSizing: 'border-box', gap: '8px'
                      }}>
                        <Layers size={20} color="#fff" style={{filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'}} />
                        <div style={{
                          fontSize: '10px', color: '#fff', fontWeight: 'bold', textAlign: 'center',
                          display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical',
                          overflow: 'hidden', lineHeight: '1.2', padding: '0 1px',
                          textShadow: '0 1px 3px rgba(0,0,0,0.7)'
                        }}>
                          {item.title || "工作流"}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // 无缩略图：显示图标布局
                    <div style={{
                        width: '100%', height: '100%',
                        backgroundColor: 'var(--spell-node-bg)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '6px', boxSizing: 'border-box', gap: '8px'
                    }}>
                      <Layers size={20} color="var(--spell-card-fg)" />
                      <div style={{
                        fontSize: '10px', color: 'var(--spell-card-fg)', fontWeight: 'bold', textAlign: 'center',
                        display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical',
                        overflow: 'hidden', lineHeight: '1.2', padding: '0 1px'
                      }}>
                        {item.title || "工作流"}
                      </div>
                    </div>
                  )
                ) : item.type === 'text' ? (
                  // 🎨 咒语：优先显示封面，无封面时显示图标布局
                  (item.localThumbUrl || item.thumbnailUrl) ? (
                    <div style={{position: 'relative', width:'100%', height:'100%'}}>
                      {/* 背景缩略图 */}
                      <img
                          src={item.localThumbUrl || item.thumbnailUrl}
                          style={{width:'100%', height:'100%', objectFit:'cover'}}
                          alt={item.title || "咒语"}
                      />
                      {/* 叠加层：半透明遮罩 + 图标 + 标题 */}
                      <div style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          padding: '6px', boxSizing: 'border-box', gap: '8px'
                      }}>
                        <Sparkles size={18} color="#fff" strokeWidth={1.5} style={{filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'}} />
                        <div style={{
                          fontSize: '10px', color: '#fff', fontWeight: 'bold', textAlign: 'center',
                          display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical',
                          overflow: 'hidden', lineHeight: '1.2', padding: '0 1px',
                          textShadow: '0 1px 3px rgba(0,0,0,0.7)'
                        }}>
                          {item.title || "咒语"}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // 无缩略图：显示图标布局
                    <div style={{
                        width: '100%', height: '100%',
                        backgroundColor: item.bgColor ? resolveBgForTheme(item.bgColor, theme) : 'var(--spell-node-bg)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '6px', boxSizing: 'border-box', gap: '8px'
                    }}>
                      <Sparkles size={18} color="var(--spell-card-fg)" strokeWidth={1.5} />
                      <div style={{
                        fontSize: '10px', color: 'var(--spell-card-fg)', fontWeight: 'bold', textAlign: 'center',
                        display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical',
                        overflow: 'hidden', lineHeight: '1.2', padding: '0 1px'
                      }}>
                        {item.title || "咒语"}
                      </div>
                    </div>
                  )
                ) : item.type === 'audio' ? (
                  <>
                    <AudioCard item={item} />

                    {/* 🆕 音频描述词小标签 */}
                    {!isSelectionMode && onEditDescription
                      && (item.canEdit !== undefined ? item.canEdit : true) && (
                      <div
                        style={{
                          ...descBadgeStyle(18, !!item.description),
                          position: 'absolute', bottom: '3px', right: '3px', zIndex: 10,
                        }}
                        onClick={(e) => { e.stopPropagation(); onEditDescription(item); }}
                        title={item.description ? `描述词：${item.description}` : '添加描述词'}
                        onMouseEnter={descBadgeHover.onMouseEnter(!!item.description)}
                        onMouseLeave={descBadgeHover.onMouseLeave(!!item.description)}
                      >
                        <DescriptionIcon size={13} />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <img 
                        src={item.localThumbUrl || item.thumbnailUrl || item.localCacheUrl || item.url} 
                        style={{width:'100%', height:'100%', objectFit:'cover'}} 
                        alt={item.title} 
                    />
                    {/* 左上角类型标记（自定义SVG图标，白色 10% 透明度） */}
                    {(item.type === 'image' || item.type === 'video') && (
                      <div style={{
                        position: 'absolute', top: '0', left: '0',
                        width: '100%', height: '100%',
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start',
                        padding: '2px',
                        pointerEvents: 'none', zIndex: 2
                      }}>
                        {item.type === 'video' 
                          ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256"><path fill="rgba(255,255,255,0.20)" d="M232 212h-36.2a107.94 107.94 0 1 0-67.8 24h104a12 12 0 0 0 0-24M44 128a84 84 0 1 1 84 84a84.09 84.09 0 0 1-84-84m64-44a20 20 0 1 1 20 20a20 20 0 0 1-20-20m20 108a20 20 0 1 1 20-20a20 20 0 0 1-20 20m24-64a20 20 0 1 1 20 20a20 20 0 0 1-20-20m-48 0a20 20 0 1 1-20-20a20 20 0 0 1 20 20"/></svg>
                          : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><g fill="none" stroke="rgba(255,255,255,0.20)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"><path d="M15 8h.01M4 15l4-4c.928-.893 2.072-.893 3 0l5 5"/><path d="m14 14l1-1c.928-.893 2.072-.893 3 0l2 2"/><path d="M3 12a9 9 0 0 0 9 9a9 9 0 0 0 9-9a9 9 0 0 0-9-9a9 9 0 0 0-9 9"/></g></svg>
                        }
                      </div>
                    )}
                    {item.type === 'audio' && <Music size={20} color="#666" />}
                    
                    <EditableLabel
                       title={item.title || "未命名"}
                       style={styles.cardLabel}
                       onUpdate={(newVal) => onUpdate && onUpdate(item.id, newVal)}
                       canEdit={item.canEdit !== undefined ? item.canEdit : true} // 🎬 使用资产自带权限
                    />

                    {/* 🆕 描述词小标签：抬到标题栏上方（标题栏是 bottom:0 通栏，贴底会压住） */}
                    {!isSelectionMode && onEditDescription
                      && (item.canEdit !== undefined ? item.canEdit : true) && (
                      <div
                        style={{
                          ...descBadgeStyle(18, !!item.description),
                          position: 'absolute', bottom: '18px', right: '3px', zIndex: 10,
                        }}
                        onClick={(e) => { e.stopPropagation(); onEditDescription(item); }}
                        title={item.description ? `描述词：${item.description}` : '添加描述词'}
                        onMouseEnter={descBadgeHover.onMouseEnter(!!item.description)}
                        onMouseLeave={descBadgeHover.onMouseLeave(!!item.description)}
                      >
                        <DescriptionIcon size={13} />
                      </div>
                    )}
                  </>
                )}

                {/* 3. 封面按钮：仅工作流和咒语、非选择模式、仅超管可见 */}
                {!isSelectionMode && (item.type === 'workflow' || item.type === 'text') && user?.role === 'super_admin' && onSetAssetCover && (
                  <div
                    style={{
                      ...styles.removeBtn,
                      right: '22px', // 在删除按钮左侧
                    }}
                    onClick={(e) => { e.stopPropagation(); onSetAssetCover(item); }}
                    title="设置封面"
                  >
                    <Image size={11} color="rgba(255,255,255,0.85)" />
                  </div>
                )}

                {/* 🆕 4. 编辑按钮：仅后台 tab、非选择模式、支持的资产类型 */}
                {!isSelectionMode && activeTab === 'project' && onEditAsset && (
                  <div
                    style={{
                      position: 'absolute', bottom: '18px', left: '3px',
                      width: '18px', height: '18px',
                      background: 'rgba(100, 100, 100, 0.6)',
                      backdropFilter: 'blur(4px)',
                      border: '0.5px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      zIndex: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onClick={(e) => { e.stopPropagation(); onEditAsset(item); }}
                    title="编辑资产"
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(120, 120, 120, 0.8)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(100, 100, 100, 0.6)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <Pencil size={10} color="rgba(255,255,255,0.95)" />
                  </div>
                )}

                {/* 5. 删除按钮：仅非选择模式下显示，且需要有删除权限 */}
                {/* 🎬 剧组系统：优先使用资产自带的 canDelete 属性，否则使用全局 canDelete */}
                {!isSelectionMode && (item.canDelete !== undefined ? item.canDelete : canDelete) && (
                  <div
                    style={styles.removeBtn}
                    onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                    title="删除资产"
                  >
                    <X size={11} color="rgba(255,255,255,0.85)" />
                  </div>
                )}

                {/* � 系统资产徽标（线条锁头，左上角，与类型图标等大） */}
                {item.isSystem && (
                  <div style={{
                    position: 'absolute', top: '0', left: '0',
                    width: '100%', height: '100%',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start',
                    padding: '2px',
                    pointerEvents: 'none', zIndex: 3
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.20)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function AssetDrawer({
    isOpen, onClose, onToggle,
  assets = [], publicAssets = [], globalSpells = [],
  // 全局咒语分类的合并列表（含默认 prompt）；由 EditorPage 派生传入
  spellCategories = [],
    projects = [], currentProjectId,
    activeTab, onTabChange,
    user, // 🆕 接收用户信息用于获取分类配置
    onSpawnAsset, onDeleteAsset, onDeletePublicAsset, onDeleteGlobalAsset,
    onUpdateAsset, // 接收
    onUploadToPublic, onDownloadFromPublic,
    canDeletePublic = false, // 🎬 是否可以删除公共素材（仅制片人可删除）
    // 舞台管理回调
    onCreateProject,
    onRenameProject,
    onDeleteProject,
    onSetProjectCover,
    onSetAssetCover, // 🆕 资产封面编辑回调
    onUpdateAssetDescription, // 🆕 描述词编辑回调
    onEditAsset, // 🆕 编辑资产回调
    onSaveBeforeSwitch,
    // 🎬 竖屏拼屏时下方剧本大师面板的高度（含分隔条）：抽屉底部抬高这么多，
    // 让上方画布区 + 右侧抽屉成为独立区域，不与底部窗口重叠，并随拼屏大小变化
    bottomOffset = 0
}) {
  const navigate = useNavigate();
  const { startLoading } = useProjectLoading(); // 🆕 全局加载状态
  const [theme] = useTheme(); // 🎨 空白舞台缩略图占位底色按主题切换（浅色=浅蓝，同仪表盘）
  // const [activeTab, setActiveTab] = useState('project'); // 移除内部状态
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // 舞台重命名编辑状态
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editProjectName, setEditProjectName] = useState('');
  const projectInputRef = React.useRef(null);
  
  // 🆕 抽屉标题编辑状态
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const titleInputRef = useRef(null);

  // 🛡️ 系统资产删除确认
  const [systemDeleteConfirm, setSystemDeleteConfirm] = useState(null);

  // 🆕 资产封面设置状态
  const [coverAsset, setCoverAsset] = useState(null);
  const [assetImages, setAssetImages] = useState([]);
  const [showAssetImagePicker, setShowAssetImagePicker] = useState(false);
  const [assetCoverCropUrl, setAssetCoverCropUrl] = useState(null);
  const [descEditTarget, setDescEditTarget] = useState(null); // 🆕 正在编辑描述词的素材

  // 🆕 获取当前项目信息
  const currentProject = projects?.find(p => p.id === currentProjectId);
  const currentProjectName = currentProject?.name || '未命名舞台';
  
  // 🆕 标题编辑逻辑
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const startEditingTitle = () => {
    setTitleValue(currentProjectName);
    setIsEditingTitle(true);
  };

  const saveTitle = () => {
    if (!titleValue.trim()) {
      setIsEditingTitle(false);
      return;
    }
    if (titleValue !== currentProjectName && onRenameProject && currentProjectId) {
      onRenameProject(currentProjectId, titleValue);
    }
    setIsEditingTitle(false);
  };
  
  // 聚焦输入框
  React.useEffect(() => {
    if (editingProjectId && projectInputRef.current) {
      projectInputRef.current.focus();
    }
  }, [editingProjectId]);
  
  // 删除确认弹窗状态
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }

  // 🆕 本地缓存状态
  const [cachePath, setCachePath] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [showCacheConfirm, setShowCacheConfirm] = useState(false);
  
  // 🆕 初始化时验证缓存配置（检查文件夹是否真实可用）
  useEffect(() => {
    const checkCache = async () => {
      if (localCacheService.isFileSystemAccessSupported()) {
        const result = await localCacheService.validateCacheConfig();
        if (result.valid) {
          setCachePath(result.path);
        } else {
          setCachePath(null);
        }
      }
    };
    checkCache();
  }, []);

  // 🆕 监听全局缓存配置变更事件（其他组件触发重配时同步更新）
  useEffect(() => {
    const handleCacheChange = (e) => {
      if (e.detail?.valid) {
        setCachePath(e.detail.path);
      } else {
        setCachePath(null);
      }
    };
    window.addEventListener('cache-config-changed', handleCacheChange);
    return () => window.removeEventListener('cache-config-changed', handleCacheChange);
  }, []);
  
  // 🆕 配置本地缓存文件夹
  const handleConfigureCache = () => {
    setShowCacheConfirm(true);
  };
  
  const handleCacheConfirm = async () => {
    setShowCacheConfirm(false);
    setCacheLoading(true);
    const result = await localCacheService.requestCacheFolder();
    setCacheLoading(false);
    
    if (result.success) {
      setCachePath(result.path);
    } else if (result.error && result.error !== '已取消选择') {
      alert(result.error);
    }
  };

  // 切换 Tab 时重置选择
  useEffect(() => {
    setIsSelectionMode(false);
    setSelectedIds(new Set());
  }, [activeTab]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
    });
  };

  const handleAction = () => {
    const idsArray = Array.from(selectedIds);
    if (activeTab === 'project') {
        if (onUploadToPublic) onUploadToPublic(idsArray);
    } else {
      if (onDownloadFromPublic) onDownloadFromPublic(idsArray, activeTab);
    }
    // 操作后退出选择模式
    setIsSelectionMode(false);
    setSelectedIds(new Set());
  };

  const displayAssets = activeTab === 'project'
    ? assets
    : (activeTab === 'global' ? globalSpells : publicAssets);
  const rawDelete = activeTab === 'project'
    ? onDeleteAsset
    : (activeTab === 'global' ? (onDeleteGlobalAsset || onDeletePublicAsset) : onDeletePublicAsset);
  // 🎬 项目素材库可删除，公共素材库只有制片人可删除
  const canDeleteAssets = activeTab === 'project' ? true : canDeletePublic;

  // 🛡️ 删除拦截：系统资产需要确认
  const handleDelete = (assetId) => {
    const allAssets = [...assets, ...publicAssets, ...globalSpells];
    const target = allAssets.find(a => a.id === assetId);
    if (target?.isSystem) {
      setSystemDeleteConfirm({ id: assetId, title: target.title });
    } else {
      rawDelete(assetId);
    }
  };

  // 🆕 资产封面设置逻辑
  // ⚠️ 已废弃：旧的项目封面设置逻辑（16:9 图片选择器）
  // 现在使用从 EditorPage 传入的 onSetAssetCover（调用 useAssetManagement 的 handleSetAssetCover）
  const handleSetAssetCover_DEPRECATED = async (asset) => {
    if (!currentProjectId) return;

    setCoverAsset(asset);

    try {
      const token = localStorage.getItem('cineart_token');
      const response = await fetch(`/api/projects/${currentProjectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const nodes = data.nodes || [];
        const images = nodes
          .filter(n => n.data?.type === 'image' && (n.data?.localCacheUrl || n.data?.output))
          .map(n => ({
            url: n.data.localCacheUrl || n.data.output,
            title: n.data.title || '图片'
          }));

        setAssetImages(images);
        setShowAssetImagePicker(true);
      }
    } catch (err) {
      console.error('加载项目图片失败:', err);
    }
  };

  const handleSelectAssetCoverImage = (url) => {
    setShowAssetImagePicker(false);
    setAssetCoverCropUrl(url);
  };

  const handleAssetCoverSave = async (blob) => {
    if (!coverAsset) return;

    try {
      const formData = new FormData();
      const fileName = `asset-cover-${coverAsset.id}-${Date.now()}.jpg`;
      formData.append('file', blob, fileName);

      const token = localStorage.getItem('cineart_token');
      const uploadResponse = await fetch('/api/upload?type=thumbnail', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();

        if (uploadData.url) {
          const updateResponse = await fetch(`/api/assets/${coverAsset.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ thumbnail_url: uploadData.url })
          });

          if (updateResponse.ok) {
            // 通知父组件更新资产列表
            if (onUpdateAsset) {
              onUpdateAsset(coverAsset.id, coverAsset.title, uploadData.url);
            }
            alert('封面设置成功');
          }
        }
      }
    } catch (err) {
      console.error('保存封面失败:', err);
      alert('保存封面失败');
    } finally {
      handleCloseAssetCoverModals();
    }
  };

  const handleCloseAssetCoverModals = () => {
    setShowAssetImagePicker(false);
    setAssetCoverCropUrl(null);
    setCoverAsset(null);
    setAssetImages([]);
  };

  const folderProps = {
    assets: displayAssets,
    onSpawn: onSpawnAsset,
    onDelete: handleDelete,
    canDelete: canDeleteAssets, // 🎬 传递删除权限（作为默认值）
    hideSpawn: activeTab === 'public', // 🎬 剧组库隐藏「转移到舞台」，需先下载到后台再使用
    onUpdate: onUpdateAsset, // 🎬 公共库和项目库都传递 onUpdate，具体权限由每个资产的 canEdit 控制
    isSelectionMode, selectedIds, onToggleSelect: toggleSelect,
    user, // 🆕 传递用户信息用于权限判断
    onSetAssetCover: onSetAssetCover, // 🆕 传递封面设置回调（使用从 EditorPage 传入的新版本）
    onEditDescription: (item) => setDescEditTarget(item), // 🆕 点右下角小标签编辑描述词
    onEditAsset: onEditAsset, // 🆕 传递编辑资产回调
    activeTab, // 🆕 传递当前 tab（用于判断是否显示编辑按钮）
  };

  return (
    <div style={styles.drawer(isOpen, bottomOffset)}>
      {/* 悬浮开关手柄 */}
      <ToggleHandle onClick={onToggle || onClose} isOpen={isOpen} />

      <div style={{ padding: '24px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                value={titleValue}
                onChange={e => setTitleValue(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={e => e.key === 'Enter' && saveTitle()}
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '1px solid #3b82f6',
                  color: 'var(--text-primary)',
                  fontSize: '16px',
                  fontWeight: '900',
                  letterSpacing: '1px',
                  outline: 'none',
                  width: '100%',
                  maxWidth: '300px'
                }}
              />
            ) : (
              <>
                <span style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '900', letterSpacing: '1px' }}>
                  {currentProjectName}
                </span>
                <Edit2
                  size={14}
                  style={{ color: 'var(--text-muted)', cursor: 'pointer', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#3b82f6'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                  onClick={startEditingTitle}
                  title="重命名舞台"
                />
              </>
            )}
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></div>
        </div>
        <div style={{ display: 'flex', background: 'var(--tab-track-bg)', borderRadius: '8px', padding: '3px' }}>
          <div onClick={() => onTabChange('projects')} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '10px 6px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', background: activeTab === 'projects' ? 'var(--tab-active-blue)' : 'transparent', color: activeTab === 'projects' ? 'var(--tab-text-blue)' : 'var(--text-muted)' }}><FolderOpen size={14} /> 舞台</div>
          <div onClick={() => onTabChange('project')} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '10px 6px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', background: activeTab === 'project' ? 'var(--tab-active-neutral)' : 'transparent', color: activeTab === 'project' ? 'var(--tab-text-white)' : 'var(--text-muted)' }}><Briefcase size={14} /> 后台</div>
          <div onClick={() => onTabChange('public')} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '10px 6px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', background: activeTab === 'public' ? 'var(--tab-active-red)' : 'transparent', color: activeTab === 'public' ? 'var(--tab-text-red)' : 'var(--text-muted)' }}><Globe size={14} /> 剧组库</div>
          <div onClick={() => onTabChange('global')} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '10px 6px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', background: activeTab === 'global' ? 'var(--tab-active-orange)' : 'transparent', color: activeTab === 'global' ? 'var(--tab-text-orange)' : 'var(--text-muted)' }}><Sparkles size={14} /> 全局</div>
        </div>
      </div>

      <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', paddingBottom: '160px' }}> {/* 增加底部 padding 给按钮+缓存配置腾位置 */}
        
        {/* 舞台目录 Tab */}
        {activeTab === 'projects' ? (
          <div style={{ padding: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
              {projects.map(p => {
                const isEditing = editingProjectId === p.id;
                const isCurrent = p.id === currentProjectId;
                
                const startEditing = (e) => {
                  e.stopPropagation();
                  setEditingProjectId(p.id);
                  setEditProjectName(p.name);
                };
                
                const saveProjectName = async () => {
                  if (!editProjectName.trim()) {
                    setEditingProjectId(null);
                    return;
                  }
                  if (editProjectName !== p.name && onRenameProject) {
                    await onRenameProject(p.id, editProjectName);
                  }
                  setEditingProjectId(null);
                };
                
                const handleDelete = (e) => {
                  e.stopPropagation();
                  // 使用自定义确认弹窗
                  setDeleteConfirm({ id: p.id, name: p.name });
                };
                
                const handleSetCover = (e) => {
                  e.stopPropagation();
                  if (onSetProjectCover) onSetProjectCover(p);
                };
                
                // 🆕 点击卡片前先保存正在编辑的项目
                const handleCardClick = async () => {
                  // 如果正在编辑自己，点击空白处应该保存并关闭编辑
                  if (isEditing) {
                    await saveProjectName();
                    return;
                  }
                  
                  // 如果有其他项目正在编辑，先保存它
                  if (editingProjectId && editingProjectId !== p.id) {
                    const editingProject = projects.find(proj => proj.id === editingProjectId);
                    if (editingProject && editProjectName.trim() && editProjectName !== editingProject.name) {
                      if (onRenameProject) {
                        await onRenameProject(editingProjectId, editProjectName);
                      }
                    }
                    setEditingProjectId(null);
                  }
                  
                  // 导航到新舞台前先保存当前舞台
                  if (p.id !== currentProjectId) {
                    if (onSaveBeforeSwitch) {
                      const allowed = await onSaveBeforeSwitch();
                      if (allowed === false) return; // 裁剪中等场景拒绝切换
                    }
                    startLoading(p.id); // 🆕 显示全局加载反馈
                    setTimeout(() => navigate(`/editor/${p.id}`), 0);
                  }
                };
                
                return (
                  <div 
                    key={p.id}
                    className="project-card-drawer"
                    onClick={handleCardClick}
                    style={{
                      background: isCurrent ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255,255,255,0.05)',
                      border: isCurrent ? '2px solid #3b82f6' : '1px solid var(--border-subtle)',
                      borderRadius: 8,
                      overflow: 'hidden',
                      cursor: isCurrent ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                    }}
                    onMouseEnter={e => {
                      if (!isCurrent) {
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                        e.currentTarget.style.transform = 'scale(1.02)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isCurrent) {
                        e.currentTarget.style.borderColor = 'var(--border-subtle)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {/* 缩略图 */}
                    <div style={{ height: 65, background: '#27272a', overflow: 'hidden', position: 'relative' }}>
                      {p.thumbnail ? (
                        <img src={p.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{
                          width: '100%', height: '100%',
                          background: (theme === 'light'
                            ? [
                                'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 50%, #93c5fd 100%)',
                                'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 50%, #b0bfd0 100%)',
                                'linear-gradient(135deg, #cfe8ff 0%, #a9d3f5 50%, #7fbce8 100%)',
                                'linear-gradient(135deg, #dde7f0 0%, #c3d4e3 50%, #a3bcd0 100%)',
                              ]
                            : [
                                'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #3b5998 100%)',
                                'linear-gradient(135deg, #2d3748 0%, #4a5568 50%, #5a6a7a 100%)',
                                'linear-gradient(135deg, #1a365d 0%, #2c5282 50%, #3182ce 100%)',
                                'linear-gradient(135deg, #2d3e50 0%, #34495e 50%, #4a6274 100%)',
                              ])[p.id % 4],
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: theme === 'light' ? 'rgba(30,58,95,0.35)' : 'rgba(255,255,255,0.3)'
                        }}>
                          <LayoutGrid size={24} strokeWidth={1} />
                        </div>
                      )}
                      {/* 操作按钮 - 悬浮显示 */}
                      <div className="project-actions" style={{ 
                        position: 'absolute', top: 4, right: 4,
                        display: 'flex', gap: 4, opacity: 0, transition: 'opacity 0.2s'
                      }}>
                        <button
                          onClick={handleSetCover}
                          title="设置封面"
                          style={{
                            background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff',
                            padding: 4, borderRadius: 4, cursor: 'pointer', display: 'flex'
                          }}
                        >
                          <Image size={12} />
                        </button>
                        <button
                          onClick={handleDelete}
                          title="删除舞台"
                          style={{
                            background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff',
                            padding: 4, borderRadius: 4, cursor: 'pointer', display: 'flex'
                          }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    {/* 信息 */}
                    <div style={{ padding: '6px 8px' }}>
                      <div style={{ 
                        fontSize: 11, fontWeight: 'bold', color: 'var(--text-primary)',
                        marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4
                      }}>
                        {isCurrent && <span style={{ color: '#3b82f6', flexShrink: 0 }}>●</span>}
                        {isEditing ? (
                          <input
                            ref={projectInputRef}
                            value={editProjectName}
                            onChange={e => setEditProjectName(e.target.value)}
                            onKeyDown={e => { 
                              if (e.key === 'Enter') saveProjectName();
                              if (e.key === 'Escape') {
                                setEditingProjectId(null);
                                setEditProjectName('');
                              }
                            }}
                            onClick={e => e.stopPropagation()}
                            onMouseDown={e => e.stopPropagation()}
                            style={{
                              flex: 1, minWidth: 0,
                              background: '#000', border: '1px solid #3b82f6', color: 'var(--text-primary)',
                              padding: '2px 4px', borderRadius: 2, outline: 'none', fontSize: 11
                            }}
                          />
                        ) : (
                          <>
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }}>
                              {p.name}
                            </span>
                            <button
                              onClick={startEditing}
                              title="重命名"
                              style={{
                                background: 'transparent', border: 'none', color: 'var(--text-muted)',
                                padding: 2, cursor: 'pointer', display: 'flex', flexShrink: 0
                              }}
                              onMouseEnter={e => e.currentTarget.style.color = 'var(--text-hover)'}
                              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                            >
                              <Pencil size={10} />
                            </button>
                          </>
                        )}
                      </div>
                      <div style={{ fontSize: 9, color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span>创建:</span>
                          <span>{new Date(p.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span>修改:</span>
                          <span>{new Date(p.updated_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* 🆕 项目状态指示器 - 卡片右下角 */}
                    <ProjectStatusIndicator 
                      pendingTasks={p.pendingTasks} 
                      hasUnseenCompleted={p.hasUnseenCompleted} 
                      size="small"
                    />
                  </div>
                );
              })}
            </div>
            {projects.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                暂无舞台
              </div>
            )}
            
            {/* 新建舞台按钮 */}
            <div style={{ marginTop: 16, padding: '0 4px' }}>
              <button
                onClick={onCreateProject}
                style={{
                  width: '100%', height: 40, borderRadius: 8,
                  background: 'rgba(37, 99, 235, 0.2)', border: '1px dashed rgba(59, 130, 246, 0.5)',
                  color: '#93c5fd', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(37, 99, 235, 0.3)';
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(37, 99, 235, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                }}
              >
                <Plus size={16} /> 新建舞台
              </button>
            </div>
            
            {/* CSS for hover effects */}
            <style>{`
              .project-card-drawer:hover .project-actions { opacity: 1 !important; }
              .project-card-drawer .project-actions button:hover { background: #3b82f6 !important; }
              .project-card-drawer .project-actions button[title="删除舞台"]:hover { background: #ef4444 !important; }
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : (
          <>
            {/* 【修改】移除大标题，用图标替换 LibraryFolder 的 icon */}

            {/* 1. 咒语库分组 */}
            {activeTab === 'global' ? (
              // 全局 tab：按 EditorPage 派生的 spellCategories（已含 prompt + 自定义，按服务端排序）渲染
              (spellCategories.length > 0
                ? spellCategories
                : ['prompt']
              ).map(cat => (
                <LibraryFolder
                  key={`spell-${cat}`}
                  title={cat === 'prompt' ? '咒语库 (Prompts)' : cat}
                  categoryId={cat}
                  icon={IconPhoto}
                  forcePrompt
                  {...folderProps}
                />
              ))
            ) : (
              // 项目库 / 剧组库 tab：仍只显示默认咒语库
              <LibraryFolder title="咒语库 (Prompts)" categoryId="prompt" icon={IconPhoto} {...folderProps} />
            )}

        {/* 2. 图像类 */}
        <LibraryFolder title="角色库 (Roles)" categoryId="role" icon={IconPhoto} {...folderProps} />
        <LibraryFolder title="场景库 (Scenes)" categoryId="scene" icon={IconPhoto} {...folderProps} />
        <LibraryFolder title="道具库 (Props)" categoryId="prop" icon={IconPhoto} {...folderProps} />

        {/* 3. 视频类 */}
        <LibraryFolder title="动作库 (Motion)" categoryId="motion" icon={IconFilm} {...folderProps} />
        <LibraryFolder title="特效库 (VFX)" categoryId="vfx" icon={IconFilm} {...folderProps} />

        {/* 4. 音频类 */}
        <LibraryFolder title="音乐库 (Music)" categoryId="music" icon={IconMusic} {...folderProps} />
        <LibraryFolder title="音效库 (SFX)" categoryId="sfx" icon={IconMusic} {...folderProps} />
        <LibraryFolder title="音色库 (Voice)" categoryId="voice" icon={IconMusic} {...folderProps} />

        {/* 5. 工作流库 */}
        <LibraryFolder title="工作流库 (Workflows)" categoryId="default" icon={Layers} {...folderProps} />
          </>
        )}
      </div>

      {/* 底部固定区域容器 */}
      {activeTab !== 'projects' && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'var(--toolbar-bg)',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 30
        }}>
          {/* 选择资产按钮区域 */}
          <div style={{
            padding: '12px 20px',
            display: 'flex', gap: '12px',
            borderBottom: 'none'
          }}>
            <button 
              onClick={() => {
                  if (isSelectionMode) {
                      setIsSelectionMode(false);
                      setSelectedIds(new Set());
                  } else {
                      setIsSelectionMode(true);
                  }
              }}
              style={{
                  flex: 1, height: '36px', borderRadius: '6px', border: '1px solid var(--border-strong)',
                  background: isSelectionMode ? 'var(--bg-hover)' : 'transparent',
                  color: 'var(--text-body)', fontSize: '12px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
              }}
            >
                {isSelectionMode ? <X size={14} /> : <MousePointerClick size={14} />}
                {isSelectionMode ? "取消选择" : "选择资产"}
            </button>
            
            {isSelectionMode && (
                <button 
                  onClick={handleAction}
                  disabled={selectedIds.size === 0}
                  style={{
                      flex: 1, height: '36px', borderRadius: '6px', border: 'none',
                      background: selectedIds.size > 0 ? '#3b82f6' : 'rgba(59, 130, 246, 0.3)',
                      color: selectedIds.size > 0 ? '#fff' : 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 'bold', cursor: selectedIds.size > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                  }}
                >
                    {activeTab === 'project' ? <UploadCloud size={14} /> : <DownloadCloud size={14} />}
                    {activeTab === 'project' ? "上传" : "下载"} ({selectedIds.size})
                </button>
            )}
          </div>

          {/* 🆕 本地缓存配置区域 - 在最底部 */}
          {localCacheService.isFileSystemAccessSupported() && (
            <div style={{ padding: '10px 20px 12px' }}>
              <div style={{
                background: cachePath ? 'var(--cache-box-bg)' : 'rgba(127, 29, 29, 0.3)',
                border: cachePath ? '1px solid var(--cache-box-border)' : '1px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '8px',
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <HardDrive size={18} color={cachePath ? 'var(--text-secondary)' : '#f87171'} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '11px',
                    color: cachePath ? 'var(--text-secondary)' : '#fca5a5',
                    fontWeight: 'bold',
                    marginBottom: '2px'
                  }}>
                    本地缓存
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: cachePath ? 'var(--text-body)' : '#94a3b8',
                    wordBreak: 'break-all',
                    lineHeight: '1.3'
                  }}>
                    {cachePath ? cachePath : '未配置 - 媒体文件每次从服务器加载'}
                  </div>
                </div>
                <button
                  onClick={handleConfigureCache}
                  disabled={cacheLoading}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: cachePath ? '1px solid var(--cache-box-border)' : '1px solid rgba(239, 68, 68, 0.5)',
                    background: cachePath ? 'var(--bg-hover)' : 'rgba(239, 68, 68, 0.2)',
                    color: cachePath ? 'var(--text-body)' : '#fca5a5',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: cacheLoading ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => { if (!cacheLoading) e.currentTarget.style.background = cachePath ? 'var(--btn-hover-solid)' : 'rgba(239, 68, 68, 0.3)'; }}
                  onMouseLeave={e => { if (!cacheLoading) e.currentTarget.style.background = cachePath ? 'var(--bg-hover)' : 'rgba(239, 68, 68, 0.2)'; }}
                >
                  {cacheLoading ? (
                    <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <RefreshCw size={12} />
                  )}
                  {cachePath ? '更换' : '配置'}
                </button>
              </div>
            </div>
          )}

        </div>
      )}

      {/* 删除确认弹窗 */}
      {deleteConfirm && (
        <ConfirmModal
          title="删除舞台"
          message={`确定要删除舞台「${deleteConfirm.name}」吗？此操作无法撤销。`}
          confirmText="删除"
          cancelText="取消"
          danger={true}
          onConfirm={() => {
            if (onDeleteProject) onDeleteProject(deleteConfirm.id);
            setDeleteConfirm(null);
          }}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
      
      {/* 🆕 缓存配置确认弹窗 */}
      {showCacheConfirm && (
        <ConfirmModal
          title="配置缓存位置"
          message="浏览器将请求该文件夹权限，正常安全机制（请牢记文件夹路径，以便在剪辑软件中找到这些素材）。"
          confirmText="开始选择"
          cancelText="取消"
          onConfirm={handleCacheConfirm}
          onCancel={() => setShowCacheConfirm(false)}
        />
      )}

      {/* 🛡️ 系统资产删除确认弹窗 */}
      {systemDeleteConfirm && (
        <ConfirmModal
          title="删除系统资源"
          message={`「${systemDeleteConfirm.title || '未命名'}」是系统资源，删除后所有用户都将无法使用。确认要删除吗？`}
          confirmText="确认删除"
          cancelText="取消"
          danger={true}
          onConfirm={() => {
            rawDelete(systemDeleteConfirm.id);
            setSystemDeleteConfirm(null);
          }}
          onCancel={() => setSystemDeleteConfirm(null)}
        />
      )}

      {/* 🆕 描述词编辑弹窗 */}
      {descEditTarget && (
        <DescriptionEditModal
          item={descEditTarget}
          onSave={(newDesc) => onUpdateAssetDescription && onUpdateAssetDescription(descEditTarget.id, newDesc)}
          onCancel={() => setDescEditTarget(null)}
        />
      )}

      {/* 🆕 资产封面图片选择器 */}
      {showAssetImagePicker && (
        <ImagePickerModal
          images={assetImages}
          onSelect={handleSelectAssetCoverImage}
          onClose={handleCloseAssetCoverModals}
        />
      )}

      {/* 🆕 资产封面裁剪器 */}
      {assetCoverCropUrl && (
        <CoverCropperModal
          imageUrl={assetCoverCropUrl}
          onSave={handleAssetCoverSave}
          onCancel={handleCloseAssetCoverModals}
        />
      )}
    </div>
  );
}